import * as React from 'react';
import { useMutation, useQuery } from 'convex/react';
import { useParams } from '@tanstack/react-router';
import { api } from '@/convex';
import { useClerkUser } from '@/hooks/useClerkUser';
import type { Id } from '@/convex';
import type { PlanId } from '../../convex/billing/plans';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrgData {
  _id: Id<'orgs'>;
  name: string;
  slug: string;
  clerkOrgId: string;
  plan: PlanId;
  logoStorageId?: Id<'_storage'>;
  logoUrl: string | null;
  createdAt: number;
}

interface MembershipData {
  _id: Id<'memberships'>;
  orgId: Id<'orgs'>;
  clerkUserId: string;
  role: 'owner' | 'admin' | 'member';
  createdAt: number;
}

interface OrgContextValue {
  orgId: Id<'orgs'>;
  org: OrgData;
  isLoading: false;
  // Multi-org
  orgs: Array<{ membership: MembershipData; org: OrgData }>;
  activeRole: 'owner' | 'admin' | 'member';
  setActiveOrg: (orgId: Id<'orgs'>) => void;
}

interface OrgContextLoading {
  orgId: undefined;
  org: undefined;
  isLoading: true;
  orgs: [];
  activeRole: undefined;
  setActiveOrg: (orgId: Id<'orgs'>) => void;
}

type OrgContextState = OrgContextValue | OrgContextLoading;

export type {
  OrgData,
  MembershipData,
  OrgContextValue,
  OrgContextLoading,
  OrgContextState,
};

// ---------------------------------------------------------------------------
// Pure helper — testable without React
// ---------------------------------------------------------------------------

export const ACTIVE_ORG_STORAGE_KEY = 'fragments-cloud:activeOrgId';

export function resolveActiveOrg(
  memberships: Array<{ membership: MembershipData; org: OrgData }>,
  savedOrgId: string | null,
  pathOrgSlug?: string,
): { org: OrgData; membership: MembershipData } | null {
  if (memberships.length === 0) return null;

  if (pathOrgSlug) {
    const pathMatch = memberships.find((m) => m.org.slug === pathOrgSlug);
    return pathMatch
      ? { org: pathMatch.org, membership: pathMatch.membership }
      : null;
  }

  // Try saved org first
  if (savedOrgId) {
    const match = memberships.find((m) => m.org._id === savedOrgId);
    if (match) return { org: match.org, membership: match.membership };
  }

  // Fallback to first org
  return { org: memberships[0].org, membership: memberships[0].membership };
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const OrgContext = React.createContext<OrgContextState | null>(null);

/**
 * Access the multi-org context.
 * Must be used within <OrgProvider>.
 */
export function useOrgContext(): OrgContextState {
  const ctx = React.useContext(OrgContext);
  if (ctx === null) {
    throw new Error('useOrgContext must be used within <OrgProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface OrgProviderProps {
  children: React.ReactNode;
}

export function OrgProvider({ children }: OrgProviderProps) {
  const { user, isLoaded: isUserLoaded } = useClerkUser();
  const params = useParams({ strict: false }) as { orgSlug?: string };
  const getOrCreateOrg = useMutation(api.orgs.getOrCreate);

  const clerkUserId = user?.id;

  // Query memberships reactively
  const memberships = useQuery(
    api.memberships.listByUser,
    clerkUserId ? { clerkUserId } : 'skip',
  );

  // Track whether we've triggered auto-provision
  const [hasProvisioned, setHasProvisioned] = React.useState(false);

  // Active org selection
  const [activeOrgId, setActiveOrgIdState] = React.useState<string | null>(
    () => {
      if (typeof window === 'undefined') return null;
      return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    },
  );

  const setActiveOrg = React.useCallback((orgId: Id<'orgs'>) => {
    setActiveOrgIdState(orgId);
    if (typeof window !== 'undefined') {
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
    }
  }, []);

  // Auto-provision: if memberships query returns empty, trigger getOrCreate
  React.useEffect(() => {
    if (!isUserLoaded || !user || hasProvisioned) return;
    if (memberships === undefined) return; // still loading
    if (memberships.length > 0) return; // already has memberships

    let cancelled = false;

    async function provision() {
      const clerkOrgId = user!.id;
      const name =
        user!.fullName ?? user!.primaryEmailAddress?.emailAddress ?? 'My Org';
      const slug = clerkOrgId.toLowerCase().replace(/[^a-z0-9-]/g, '-');

      await getOrCreateOrg({ clerkOrgId, name, slug, clerkUserId: user!.id });
      if (!cancelled) setHasProvisioned(true);
    }

    provision();

    return () => {
      cancelled = true;
    };
  }, [isUserLoaded, user, memberships, hasProvisioned, getOrCreateOrg]);

  // Resolve active org from memberships
  const orgs = (memberships ?? []) as Array<{
    membership: MembershipData;
    org: OrgData;
  }>;
  const resolved = resolveActiveOrg(orgs, activeOrgId, params?.orgSlug);

  const isLoading =
    !isUserLoaded ||
    memberships === undefined ||
    (orgs.length === 0 && !hasProvisioned);

  const value: OrgContextState =
    isLoading || !resolved
      ? {
          orgId: undefined,
          org: undefined,
          isLoading: true,
          orgs: [],
          activeRole: undefined,
          setActiveOrg,
        }
      : {
          orgId: resolved.org._id,
          org: resolved.org,
          isLoading: false,
          orgs,
          activeRole: resolved.membership.role,
          setActiveOrg,
        };

  return React.createElement(OrgContext.Provider, { value }, children);
}
